import { create } from 'zustand';
import type { ExecutionData } from '../api/types';

/**
 * Recent strategy EXECUTION outcomes, scoped per portfolioId. Fed by the
 * `rebalances` WS channel (same channel as rebalances): an `execution-snapshot`
 * on subscribe and an `execution` push per new outcome. Drives the Strategy
 * Activity → Executions tab.
 *
 * Newest-first, bounded. A convenience timeline, not a durable record.
 */

// Matches the gateway ring (DataCache.MAX_EXECUTIONS_PER_PORTFOLIO).
const MAX_PER_PORTFOLIO = 60;

const EMPTY: ExecutionData[] = [];

type State = {
  byPortfolio: Record<string, ExecutionData[]>;
  setSnapshot: (executions: ExecutionData[]) => void;
  add: (execution: ExecutionData) => void;
};

export const useExecutionsStore = create<State>((set) => ({
  byPortfolio: {},

  setSnapshot: (executions) =>
    set((state) => {
      if (executions.length === 0) return state;
      const portfolioId = executions[0]!.portfolioId;
      const sorted = [...executions]
        .sort((a, b) => b.executedAt - a.executedAt)
        .slice(0, MAX_PER_PORTFOLIO);
      return { byPortfolio: { ...state.byPortfolio, [portfolioId]: sorted } };
    }),

  add: (execution) =>
    set((state) => {
      const list = state.byPortfolio[execution.portfolioId] ?? [];
      // Dedup on (kind, symbol, executedAt) — the same outcome can re-arrive on
      // reconnect (snapshot + live push race).
      const exists = list.some(
        (e) =>
          e.kind === execution.kind &&
          e.symbol === execution.symbol &&
          e.executedAt === execution.executedAt,
      );
      if (exists) return state;
      const next = [execution, ...list].slice(0, MAX_PER_PORTFOLIO);
      return {
        byPortfolio: { ...state.byPortfolio, [execution.portfolioId]: next },
      };
    }),
}));

/** Stable selector: the execution list for one portfolio (newest-first). */
export function selectExecutions(
  portfolioId: string | null | undefined,
): (s: State) => ExecutionData[] {
  return (s) => (portfolioId ? s.byPortfolio[portfolioId] ?? EMPTY : EMPTY);
}
